import { Link } from '@solidjs/router';
import { For, Show, useContext } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../../../app-context-provider/app-context-provider';
import { HeartCviIcon } from '../../../icons-library/heart-cvi';
import { GlobeCviIcon } from '../../../icons-library/globe-cvi';
import { BasketCviIcon } from '../../../icons-library/basket-cvi';
import { UserIcon } from '../../../icons-library/user';
import { toggleCountrySelectionMenu } from '../../../../stores/locale-store';
import { removeTrailingSlash } from '../../../../tools/remove-trailing-slash';
import removeAmpersand from '../../../../tools/remove-ampersand';
import urlMatches from '../../../../tools/url-match';
import { isExternalLink } from '../../../../tools/check-is-external-url';
import { createUserAccount } from '../../../login-controls/create-user-account';
import type { LinkType } from '../../menu-types';

type MenuUtilitiesProps = {
    headerLinks: LinkType[];
    utilityLinks: LinkType[];
    onLinkClick: () => void;
};

export const MenuUtilities = (props: MenuUtilitiesProps) => {
    const { appStore, userProfileUrls, siteInfo, localize, pageRegistration, shopPage } = useContext(AppContext);
    const { loggedIn, getInitials, handleClick: handleAccountClick, shouldShowLoginAffordance } = createUserAccount();

    /* Hide the account row when there's no affordance to show:
       - Logged-out users on default sites in PAC mode (rule from createUserAccount)
       - Or when registration isn't configured for this site at all. */
    const showAccountRow = () => Boolean(pageRegistration.url) && (loggedIn() || shouldShowLoginAffordance());

    const rowBase = 'flex items-center self-center gap-cvi-5 w-full min-h-cvi-touch-min p-cvi-4 label-lg leading-none text-cvi-fg-default no-underline transition-colors duration-200 hover:text-cvi-fg-link';

    return (
        <div class="flex flex-col">
            <div class="flex flex-col mx-[calc(50%-50vw)] px-[calc(50vw-50%)]">
                <For each={props.utilityLinks}>
                    {(link) => {
                        const isActive = () => urlMatches(link.url);
                        return (
                            <Show when={link && link.url?.trim() && link.url.trim() !== '#' && link.title}>
                                <Link
                                    href={removeTrailingSlash(link.url)}
                                    onClick={() => props.onLinkClick()}
                                    class={clsx(
                                        rowBase,
                                        isActive() ? 'font-cvi-bold' : 'font-cvi-medium'
                                    )}
                                    {...(isExternalLink(link.url)
                                        ? { target: '_blank', rel: 'noopener noreferrer' }
                                        : (link.target ? { target: link.target } : {}))}
                                >
                                    <Show when={isActive()}>
                                        <span
                                            aria-hidden="true"
                                            class="size-cvi-3 rounded-cvi-round bg-cvi-fg-brand shrink-0"
                                        />
                                    </Show>
                                    {removeAmpersand(link.title)}
                                </Link>
                            </Show>
                        );
                    }}
                </For>
            </div>
            <div class="flex flex-col mt-cvi-6 pt-cvi-6 border-t border-cvi-border-muted mx-[calc(50%-50vw)] px-[calc(50vw-50%)]">
                <Show when={showAccountRow()}>
                    <Show
                        when={loggedIn()}
                        fallback={
                            <button
                                type="button"
                                onClick={handleAccountClick}
                                class={clsx(rowBase, 'font-cvi-medium bg-transparent border-0 cursor-pointer text-left')}
                            >
                                <span class="flex items-center justify-center size-cvi-icon-box shrink-0 text-cvi-fg-default">
                                    <UserIcon size="lg" fill="currentColor" />
                                </span>
                                <span class="pt-cvi-2">{localize('login-register', 'Log in / Register')}</span>
                            </button>
                        }
                    >
                        <Link
                            href={removeTrailingSlash(userProfileUrls.account)}
                            onClick={() => props.onLinkClick()}
                            class={clsx(rowBase, 'font-cvi-medium')}
                        >
                            <span class="flex items-center justify-center size-cvi-icon-box shrink-0 rounded-cvi-round bg-cvi-fg-default text-cvi-fg-inverse label-sm font-cvi-medium pt-cvi-2">
                                {getInitials()}
                            </span>
                            <span class="pt-cvi-2">{localize('header-my-account', 'My account')}</span>
                        </Link>
                    </Show>
                </Show>

                <Show when={siteInfo.siteType === 'default'}>
                    <Link
                        href={userProfileUrls.bookmarks}
                        onClick={() => props.onLinkClick()}
                        class={clsx(rowBase, 'font-cvi-medium')}
                    >
                        <span class="flex items-center justify-center size-cvi-icon-box shrink-0 text-cvi-fg-default">
                            <HeartCviIcon size="lg" />
                        </span>
                        <span class="pt-cvi-2">{localize('header_my_content_icon', 'My content')}</span>
                    </Link>
                </Show>

                <button
                    type="button"
                    onClick={() => {
                        toggleCountrySelectionMenu();
                        props.onLinkClick();
                    }}
                    class={clsx(
                        rowBase,
                        'font-cvi-medium bg-transparent border-0 cursor-pointer text-left'
                    )}
                >
                    <span class="flex items-center justify-center size-cvi-icon-box shrink-0 text-cvi-fg-default">
                        <GlobeCviIcon size="lg" />
                    </span>
                    <span class="pt-cvi-2">{localize('change-language', 'Change language')}</span>
                </button>

                <Show when={shopPage?.url && shopPage.url !== ''}>
                    <Link
                        href={removeTrailingSlash(shopPage.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => props.onLinkClick()}
                        class={clsx(rowBase, 'font-cvi-medium')}
                    >
                        <span class="flex items-center justify-center size-cvi-icon-box shrink-0 text-cvi-fg-default">
                            <BasketCviIcon size="lg" />
                        </span>
                        <span class="pt-cvi-2">{localize('shop', 'Webshop')}</span>
                    </Link>
                </Show>
            </div>
            <div class="flex flex-col mt-cvi-6 pt-cvi-6 border-t border-cvi-border-muted mx-[calc(50%-50vw)] px-[calc(50vw-50%)]">
                <For each={props.headerLinks}>
                    {(link: LinkType, index) => {
                        if (!link?.url || !link?.title) return null;
                        /* headerLinks[0] = PAC, headerLinks[1] = HCP — same convention as desktop tabs. */
                        const isActive = () => appStore.isHcp() ? index() === 1 : index() === 0;
                        return (
                            <Link
                                href={removeTrailingSlash(link.url)}
                                onClick={() => props.onLinkClick()}
                                class={clsx(
                                    rowBase,
                                    isActive() ? 'font-cvi-bold' : 'font-cvi-medium'
                                )}
                                {...(isExternalLink(link.url)
                                    ? { target: '_blank', rel: 'noopener noreferrer' }
                                    : (link.target ? { target: link.target } : {}))}
                            >
                                <Show when={isActive()}>
                                    <span
                                        aria-hidden="true"
                                        class="size-cvi-3 rounded-cvi-round bg-cvi-fg-brand shrink-0"
                                    />
                                </Show>
                                {removeAmpersand(link.title)}
                            </Link>
                        );
                    }}
                </For>
            </div>
        </div>
    );
};
